import { cn } from '@/lib/utils';
import { Eye, EyeOff } from 'lucide-react';
import { ComponentType, useRef, useState } from 'react';
import { Control } from 'react-hook-form';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form';
import { Input, InputProps } from '../ui/input';

interface InputControlProps extends InputProps {
  control?: Control<any>;
  name: string;
  label?: string | null;
  labelClass?: string;
  inputClassName?: string;
  placeholder?: string | any;
  required?: boolean;
  isNumber?: boolean;
  maxLength?: number;
  maxValue?: number;
  withoutDescription?: boolean;
  SuffixChild?: React.ReactNode;
  PrefixChild?: React.ReactNode;
}

export const numberMask = (value: string) => {
  return value.replace(/\D/g, '');
};

export const InputControl: ComponentType<InputControlProps> = ({
  control,
  label,
  name,
  className,
  labelClass,
  inputClassName,
  type: inputType,
  required,
  isNumber,
  maxLength,
  maxValue,
  withoutDescription,
  SuffixChild,
  PrefixChild,
  ...rest
}) => {
  const [type, setType] = useState(inputType);
  const inputRef = useRef<HTMLInputElement>(null);

  const onSetType = () => {
    if (type === 'text') {
      setType('password');
    } else {
      setType('text');
    }
  };

  const onWrapperMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    inputRef.current?.focus();
  };

  return (
    <FormField
      control={control}
      name={name}
      render={({ field: { value, onChange, ...field } }) => (
        <FormItem className={cn('relative', className)}>
          {label && (
            <FormLabel className={labelClass}>
              {label} {required && <span className={cn('text-red')}>*</span>}
            </FormLabel>
          )}
          <FormControl>
            <div
              className={cn(
                'border-gray-steel relative flex h-10 w-full items-center gap-1 rounded-[100px] border bg-white',
                rest.disabled && 'bg-gray-smoke/20 opacity-50',
                rest.readOnly && 'bg-transparent',
                inputClassName,
              )}
              onClick={onWrapperMouseDown}
            >
              {PrefixChild}
              <Input
                {...rest}
                {...field}
                ref={inputRef}
                required={false}
                value={value ?? ''}
                className={cn('w-full')}
                type={type}
                maxLength={maxLength}
                onChange={(e) => {
                  if (isNumber) {
                    let maskedValue = numberMask(e.target.value);
                    if (maxLength && maskedValue.length > maxLength) {
                      return;
                    }
                    let numericValue = Number(maskedValue);
                    if (maxValue !== undefined && numericValue > maxValue) {
                      maskedValue = String(maxValue);
                    }
                    onChange(maskedValue);
                  } else {
                    onChange(e);
                  }
                }}
              />
              {inputType === 'password' ? (
                <a
                  className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer"
                  onClick={onSetType}
                >
                  {type === 'text' ? <Eye /> : <EyeOff />}
                </a>
              ) : null}
              {SuffixChild}
            </div>
          </FormControl>
          {!withoutDescription && (
            <div className="absolute -bottom-[19px] left-0">
              <FormMessage />
            </div>
          )}
        </FormItem>
      )}
    />
  );
};
